import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import { AppState, AppStateStatus } from 'react-native';
import {
    v2GetChatMessages,
    v2GetUnreadChatMessages,
    v2SendChatMessage,
    v2SendChatMessageWithAttachment,
    v2EditChatMessage,
    v2DeleteChatMessage,
    v2MarkChatRead,
    v2GetSupportUnreadCount,
} from '../services/v2/api';
import { V2ChatMessage } from '../types/v2';
import { V2ApiError } from '../services/v2/errors';
import { useAuth } from './AuthContext';
import { badgeManager } from '../services/badge';
import { notificationService } from '../services/notifications';
import { combineSupportUnreadCounts } from '../tools/supportClient';

interface ChatContextType {
    messages: V2ChatMessage[];
    unreadCount: number;
    clientSupportUnreadCount: number;
    supportUnreadCount: number;
    isLoading: boolean;
    isSending: boolean;
    error: string | null;
    isChatActive: boolean;
    refreshMessages: () => Promise<void>;
    sendMessage: (content: string) => Promise<boolean>;
    sendMessageWithAttachment: (content: string | null, file: {
        uri: string;
        name: string;
        type: string;
    }) => Promise<boolean>;
    /** Редактирует своё сообщение. Возвращает true при успехе, false при ошибке. */
    editMessage: (messageId: string, content: string) => Promise<boolean>;
    /** Soft-удаляет своё сообщение. После успеха message.deleted_at заполнен,
     *  content становится null — UI должен показать tombstone. */
    deleteMessage: (messageId: string) => Promise<boolean>;
    markAsRead: () => Promise<void>;
    refreshClientSupportUnreadCount: () => Promise<void>;
    setChatActive: (active: boolean) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

const POLLING_INTERVAL = 15000; // 15 seconds

/**
 * Whether a chat request's failure means the session is over: the server
 * answered 401. Read from the error's type, not its words — the old match on
 * "Firebase" or "authentication" anywhere in the text signed a super-user out
 * when a poll caught the Firebase sign-in switching to the client's account.
 */
const isSessionExpiredError = (err: unknown): boolean =>
    err instanceof V2ApiError && err.status === 401 && err.code !== 'invalid_credentials';

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, handleSessionExpired, user } = useAuth();
    const [messages, setMessages] = useState<V2ChatMessage[]>([]);
    const [unreadCount, setUnreadCount] = useState<number>(0);
    const [clientSupportUnreadCount, setClientSupportUnreadCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSending, setIsSending] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isChatActive, setIsChatActive] = useState<boolean>(false);

    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const appStateRef = useRef<AppStateStatus>(AppState.currentState);
    const isChatActiveRef = useRef<boolean>(false);
    const isDirectSuperUser = user?.is_super_user === true && !user?.is_impersonating;

    // Who a request was sent for. An answer that arrives after another user
    // took over — "Login as user" or its end — is about the previous session
    // and must not end the new one.
    const userIdRef = useRef<string | null>(user?.id ?? null);
    useEffect(() => {
        userIdRef.current = user?.id ?? null;
    }, [user?.id]);
    const isSessionExpired = useCallback(
        (err: unknown, startedFor: string | null) =>
            startedFor === userIdRef.current && isSessionExpiredError(err),
        [],
    );

    // Keep ref in sync with state for use in callbacks
    useEffect(() => {
        isChatActiveRef.current = isChatActive;
    }, [isChatActive]);

    // Fetch all messages
    const refreshMessages = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }

        const startedFor = userIdRef.current;
        try {
            setError(null);
            const response = await v2GetChatMessages();
            setMessages(response.messages || []);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch messages';
            console.error('Failed to fetch messages:', err);

            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return;
            }

            setError(errorMessage);
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Fetch unread count
    const fetchUnreadCount = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }

        const startedFor = userIdRef.current;
        try {
            const response = await v2GetUnreadChatMessages();
            setUnreadCount(response.total || 0);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch unread count';
            console.error('Failed to fetch unread count:', err);

            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return;
            }
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    const refreshClientSupportUnreadCount = useCallback(async () => {
        if (!isAuthenticated || !isDirectSuperUser) {
            setClientSupportUnreadCount(0);
            return;
        }

        const startedFor = userIdRef.current;
        try {
            setClientSupportUnreadCount(await v2GetSupportUnreadCount());
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch client support unread count';
            console.error('Failed to fetch client support unread count:', err);
            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
            }
        }
    }, [isAuthenticated, isDirectSuperUser, handleSessionExpired, isSessionExpired]);

    // Initial load
    const loadInitialData = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }

        setIsLoading(true);
        try {
            await Promise.all([
                refreshMessages(),
                fetchUnreadCount(),
                refreshClientSupportUnreadCount(),
            ]);
        } finally {
            setIsLoading(false);
        }
    }, [isAuthenticated, refreshMessages, fetchUnreadCount, refreshClientSupportUnreadCount]);

    // Polling function - fetch unread count, and also messages if chat is active
    const poll = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }

        if (isChatActiveRef.current) {
            // When chat is active, refresh messages and mark as read.
            // Skip mark-as-read under impersonation: backend forbids it (the
            // target user would otherwise see "read" state from a session they
            // didn't initiate). Server capability flag is authoritative.
            await refreshMessages();
            const canMarkRead = user?.capabilities?.can_mark_chat_read ?? true;
            if (canMarkRead) {
                await v2MarkChatRead();
                setUnreadCount(0);
            }
        } else {
            // When chat is not active, just fetch unread count
            await fetchUnreadCount();
        }
        await refreshClientSupportUnreadCount();
    }, [isAuthenticated, fetchUnreadCount, refreshMessages, refreshClientSupportUnreadCount, user?.capabilities?.can_mark_chat_read]);

    // Start polling
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }
        pollingIntervalRef.current = setInterval(poll, POLLING_INTERVAL);
    }, [poll]);

    // Stop polling
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    // Handle app state changes
    useEffect(() => {
        const subscription = AppState.addEventListener('change', (nextAppState) => {
            if (
                appStateRef.current.match(/inactive|background/) &&
                nextAppState === 'active'
            ) {
                // App came to foreground
                if (isAuthenticated) {
                    poll();
                    startPolling();
                }
            } else if (nextAppState.match(/inactive|background/)) {
                // App went to background
                stopPolling();
            }
            appStateRef.current = nextAppState;
        });

        return () => {
            subscription.remove();
        };
    }, [isAuthenticated, poll, startPolling, stopPolling]);

    // Effect to handle authentication state changes
    useEffect(() => {
        if (isAuthenticated) {
            loadInitialData();
            startPolling();
        } else {
            setMessages([]);
            setUnreadCount(0);
            setClientSupportUnreadCount(0);
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [isAuthenticated, loadInitialData, startPolling, stopPolling]);

    // Мгновенный refresh при получении пуша о новом сообщении
    useEffect(() => {
        notificationService.setOnChatMessageHandler(() => {
            if (isChatActiveRef.current) {
                refreshMessages();
            } else {
                fetchUnreadCount();
            }
        });
    }, [refreshMessages, fetchUnreadCount]);

    // Send a text message
    const sendMessage = useCallback(async (content: string): Promise<boolean> => {
        if (!isAuthenticated || !content.trim()) {
            return false;
        }

        setIsSending(true);
        const startedFor = userIdRef.current;
        try {
            const newMessage = await v2SendChatMessage(content.trim());
            setMessages((prev) => [...prev, newMessage]);
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
            console.error('Failed to send message:', err);

            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return false;
            }

            setError(errorMessage);
            return false;
        } finally {
            setIsSending(false);
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Send a message with attachment
    const sendMessageWithAttachment = useCallback(async (
        content: string | null,
        file: { uri: string; name: string; type: string }
    ): Promise<boolean> => {
        if (!isAuthenticated) {
            return false;
        }

        setIsSending(true);
        const startedFor = userIdRef.current;
        try {
            const newMessage = await v2SendChatMessageWithAttachment(content, file);
            setMessages((prev) => [...prev, newMessage]);
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
            console.error('Failed to send message with attachment:', err);

            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return false;
            }

            setError(errorMessage);
            return false;
        } finally {
            setIsSending(false);
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Edit own user-authored message. Backend проверяет ownership и
    // sender_type=user; audit-таблица (v1_2_chat_messages_hist) фиксирует
    // OLD-image автоматически через DB-trigger.
    const editMessage = useCallback(async (
        messageId: string,
        content: string,
    ): Promise<boolean> => {
        if (!isAuthenticated || !content.trim()) {
            return false;
        }
        const startedFor = userIdRef.current;
        try {
            const updated = await v2EditChatMessage(messageId, content.trim());
            setMessages((prev) =>
                prev.map((m) => (m.id === messageId ? updated : m)),
            );
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to edit message';
            console.error('Failed to edit message:', err);
            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return false;
            }
            setError(errorMessage);
            throw err;
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Soft-delete своего сообщения. Backend returns обновлённое сообщение
    // (с deleted_at + content=null); optimistic update в state.
    // throws прокидывает сообщение для caller'а — он покажет actual error
    // user'у вместо generic "Delete failed".
    const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
        if (!isAuthenticated) {
            return false;
        }
        const startedFor = userIdRef.current;
        try {
            const updated = await v2DeleteChatMessage(messageId);
            setMessages((prev) =>
                prev.map((m) => (m.id === messageId ? updated : m)),
            );
            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete message';
            console.error('Failed to delete message:', err);
            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return false;
            }
            setError(errorMessage);
            // Прокидываем error дальше, чтобы SupportScreen мог показать
            // реальное сообщение от backend (например "Chat message not
            // found" если 404 = endpoint не задеплоен или race с админом).
            throw err;
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Mark messages as read
    const markAsRead = useCallback(async () => {
        if (!isAuthenticated) {
            return;
        }

        const startedFor = userIdRef.current;
        try {
            await v2MarkChatRead();
            setUnreadCount(0);
            // Refresh the app icon badge
            await badgeManager.refreshFromBackend();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to mark as read';
            console.error('Failed to mark messages as read:', err);

            if (isSessionExpired(err, startedFor)) {
                handleSessionExpired(errorMessage);
                return;
            }
        }
    }, [isAuthenticated, handleSessionExpired, isSessionExpired]);

    // Set chat active state (called when entering/leaving Support screen)
    const setChatActive = useCallback((active: boolean) => {
        setIsChatActive(active);
        notificationService.setChatActive(active);
        if (active && isAuthenticated) {
            // Immediately refresh messages and mark as read when becoming active
            refreshMessages();
            markAsRead();
        }
    }, [isAuthenticated, refreshMessages, markAsRead]);

    const value: ChatContextType = {
        messages,
        unreadCount,
        clientSupportUnreadCount,
        supportUnreadCount: combineSupportUnreadCounts(unreadCount, clientSupportUnreadCount),
        isLoading,
        isSending,
        error,
        isChatActive,
        refreshMessages,
        sendMessage,
        sendMessageWithAttachment,
        editMessage,
        deleteMessage,
        markAsRead,
        refreshClientSupportUnreadCount,
        setChatActive,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export const useChat = (): ChatContextType => {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
};

export default ChatContext;

